import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { useEvents } from "../../../Contexts/EventsContext";
import { formatDeadlineDisplay } from "../../helpers/helpers";

const getCurrentDate = () => new Date().toISOString().split("T")[0];

const getEventStatus = (startDate, endDate) => {
  const currentDate = getCurrentDate();
  if (!startDate || !endDate) return "upcoming";
  if (currentDate < startDate) return "upcoming";
  if (currentDate >= startDate && currentDate <= endDate) return "current";
  return "past";
};

const EventsGrid = () => {
  const navigate = useNavigate();
  const { events } = useEvents();
  const carouselRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [availableFilters, setAvailableFilters] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const statusCounts = {
      all: events.length,
      upcoming: 0,
      current: 0,
      past: 0,
    };

    events.forEach((event) => {
      const status = getEventStatus(event.startDate, event.endDate);
      if (statusCounts[status] !== undefined) statusCounts[status]++;
    });

    const filters = [
      { key: "all", label: "All Events", count: statusCounts.all },
    ];

    if (statusCounts.upcoming > 0)
      filters.push({
        key: "upcoming",
        label: "Upcoming",
        count: statusCounts.upcoming,
      });
    if (statusCounts.current > 0)
      filters.push({
        key: "current",
        label: "Current",
        count: statusCounts.current,
      });
    if (statusCounts.past > 0)
      filters.push({ key: "past", label: "Past", count: statusCounts.past });

    setAvailableFilters(filters);
    setActiveFilter("all");
  }, [events]);

  useEffect(() => {
    let filtered = events.slice();

    if (activeFilter !== "all") {
      filtered = filtered.filter(
        (event) =>
          getEventStatus(event.startDate, event.endDate) === activeFilter,
      );
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (event) =>
          event.title.toLowerCase().includes(term) ||
          (event.description || "").toLowerCase().includes(term) ||
          (event.location || "").toLowerCase().includes(term),
      );
    }

    setFilteredEvents(filtered);
  }, [activeFilter, searchTerm, events]);

  const formatDate = (startDate, endDate, fallbackDate) => {
    if (!startDate || !endDate) return fallbackDate || "TBA";
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()))
      return fallbackDate || "TBA";

    const startMonth = start.toLocaleDateString("en-US", { month: "short" });
    const endMonth = end.toLocaleDateString("en-US", { month: "short" });

    if (startMonth === endMonth)
      return `${startMonth} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${start.getFullYear()}`;
  };

  const scrollCarousel = (direction) => {
    if (!carouselRef.current) return;
    const scrollAmount = 320;
    carouselRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <section className="bg-gradient-to-b from-white to-gray-50 py-16 relative overflow-hidden">
      {/* Faded EVENTS watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none z-0">
        <span className="text-[12rem] sm:text-[16rem] md:text-[20rem] font-black text-custom-blue-1/[0.03] tracking-widest uppercase leading-none">
          EVENTS
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="relative text-center mb-12">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-36 h-36 bg-custom-blue-1/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-3xl md:text-5xl font-extrabold text-custom-blue-1 mb-4">
              Discover Events
            </h1>
            <p className="text-base md:text-lg text-custom-blue-1/70 max-w-2xl mx-auto">
              Explore hackathons, bootcamps, and challenges accelerating
              technology and innovation across East Africa.
            </p>

            {/* Search */}
            <div className="max-w-md mx-auto relative mt-6">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-custom-blue-1/60" />
              </div>
              <input
                type="text"
                placeholder="Search events by title, description or location..."
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-10">
          <div className="flex justify-center border-b border-gray-200 max-w-xl mx-auto">
            {availableFilters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`relative px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                  activeFilter === filter.key
                    ? "text-custom-blue-1 font-semibold"
                    : "text-gray-600 hover:text-custom-blue-1"
                }`}
              >
                {filter.label}
                {activeFilter === filter.key && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-1.5 bg-custom-blue-1 rounded-t-md"></span>
                )}
                <span
                  className={`ml-3 inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${
                    activeFilter === filter.key
                      ? "bg-custom-blue-1 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {filter.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Carousel header + controls */}
        {filteredEvents.length > 0 && (
          <div
            className={`flex flex-col gap-4 mb-6 ${
              filteredEvents.length >= 4
                ? "md:flex-row justify-between items-start md:items-center"
                : "items-center text-center"
            }`}
          >
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-custom-blue-1 flex items-center gap-3">
                <span>
                  {activeFilter === "all"
                    ? "All Events"
                    : `${
                        activeFilter.charAt(0).toUpperCase() +
                        activeFilter.slice(1)
                      } Events`}
                </span>
                <span className="inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                  {filteredEvents.length}
                </span>
              </h2>
              {filteredEvents.length >= 4 && (
                <p className="text-sm text-gray-500 mt-1">
                  Swipe or use arrows to browse more events
                </p>
              )}
            </div>

            {filteredEvents.length >= 4 && (
              <div className="flex gap-2">
                <button
                  onClick={() => scrollCarousel("left")}
                  className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-custom-blue-1/5 transition-colors duration-200 shadow-sm"
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5 text-custom-blue-1" />
                </button>
                <button
                  onClick={() => scrollCarousel("right")}
                  className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-custom-blue-1/5 transition-colors duration-200 shadow-sm"
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5 text-custom-blue-1" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Events Carousel */}
        <div className="relative">
          <div
            ref={carouselRef}
            className={`flex gap-6 pb-6 -mx-2 px-2 ${
              filteredEvents.length >= 4
                ? "overflow-x-auto scrollbar-hide"
                : "justify-center flex-wrap"
            }`}
            style={
              filteredEvents.length >= 4
                ? { scrollbarWidth: "none", msOverflowStyle: "none" }
                : {}
            }
          >
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                className="flex-shrink-0 w-80 bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 flex flex-col h-[28rem]"
                onClick={() => navigate(`/events/${event.id}`)}
              >
                {/* Image */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={event.image || "/Images/background.jpg"}
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent"></div>

                  {/* Status badge */}
                  <div className="absolute top-3 right-3">
                    <div
                      className={`px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm ${
                        getEventStatus(event.startDate, event.endDate) ===
                        "upcoming"
                          ? "bg-custom-blue-1/90 text-white"
                          : getEventStatus(event.startDate, event.endDate) ===
                              "current"
                            ? "bg-custom-green-1/90 text-white"
                            : "bg-gray-500/90 text-white"
                      }`}
                    >
                      {getEventStatus(event.startDate, event.endDate)}
                    </div>
                  </div>

                  {/* Date */}
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {formatDate(event.startDate, event.endDate, event.date)}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="text-lg font-semibold text-custom-blue-1 mb-2 leading-tight">
                    {event.title}
                  </h3>
                  <p className="text-custom-blue-1/70 text-sm mb-4 leading-relaxed line-clamp-2">
                    {event.description}
                  </p>

                  <div className="flex items-center justify-between text-sm text-custom-blue-1/70 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-custom-green-1/10 flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-custom-green-1" />
                      </div>
                      <span className="line-clamp-1 max-w-[120px]">
                        {event.location}
                      </span>
                    </div>

                    {(event.prizes || event.funding) && (
                      <div className="text-xs font-bold px-3 py-1 rounded-full bg-custom-green-1/10 text-custom-green-1">
                        {(event.prizes || event.funding)?.split(" in ")[0]}
                      </div>
                    )}
                  </div>

                  {/* Action buttons pinned to bottom of card */}
                  <div className="mt-auto flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/${event.id}`);
                      }}
                      className={`py-3 bg-white border border-custom-green-1 text-custom-green-1 rounded-lg hover:bg-custom-green-1/5 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-custom-green-1/60 ${
                        getEventStatus(event.startDate, event.endDate) ===
                        "past"
                          ? "flex-1"
                          : "flex-1"
                      }`}
                    >
                      View Details
                    </button>
                    {getEventStatus(event.startDate, event.endDate) !==
                      "past" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (event.eventType === "grant") {
                            navigate("/");
                          } else if (event.eventType === "hackathon") {
                            navigate(`/login?event=true&eventId=${event.id}`);
                          } else {
                            // For other event types, handle RSVP
                            console.log("RSVP functionality to be implemented");
                          }
                        }}
                        className="flex-1 py-3 bg-custom-green-1 text-white rounded-lg hover:shadow-lg transition-shadow duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-custom-green-1/60"
                      >
                        {event.eventType === "hackathon" ||
                        event.eventType === "grant"
                          ? "Apply Now"
                          : "RSVP"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Empty state */}
        {filteredEvents.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100 mt-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-50 flex items-center justify-center">
              <Calendar className="w-12 h-12 text-custom-green-1/50" />
            </div>
            <h3 className="text-2xl font-bold text-custom-blue-1 mb-4">
              No Events Found
            </h3>
            <p className="text-custom-blue-1/70 text-lg max-w-lg mx-auto">
              {searchTerm
                ? `No results found for "${searchTerm}". Try a different search term or filter.`
                : `There are no ${activeFilter} events available at the moment. Please check back later or try a different filter.`}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setActiveFilter("all")}
                className="px-8 py-3 bg-custom-green-1 text-white rounded-lg hover:shadow-lg transition-all duration-200 font-medium"
              >
                View All Events
              </button>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="px-8 py-3 bg-white border border-gray-200 text-custom-blue-1 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center justify-center gap-2"
                >
                  <span>Clear Search</span>
                  <Search className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default EventsGrid;
